import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Users, Bot, Crown, ChevronRight, Globe2, Zap, Shield, Wand2, Gamepad2, MessagesSquare, Trophy } from "lucide-react";
import { buildAvatarUrl } from "@/lib/avatar";
import { getCharacter } from "@/lib/characters";

// Hero + floating mini characters (cinematic showcase)
const HERO_CHAR = getCharacter("neo-vex");
const FLOAT_CHARS = ["xenon-9", "kalva-asur", "atlas-rx"].map(getCharacter);

// Showcase strip — mix of humans and creatures (real cyberpunk portraits)
const SHOWCASE_IDS = [
  "kira-zero", "void-spawn", "atlas-rx", "kalva-asur", "ronin-7",
  "xenon-9", "kane-virex", "snow-blade", "ghost-line", "ember-skar",
  "vega-jett", "scarlet-9", "halo-neon", "ruby-flare", "razor-king",
];
const SHOWCASE_AVATARS = SHOWCASE_IDS.map((id, i) => ({ ...getCharacter(id), code: `UNIT-${String(i + 1).padStart(3, "0")}` }));

// legacy: removed bottts/pixel-art/avataaars showcase definition
const _LEGACY_REMOVED = [
  { style: "bottts", seed: "X-7-Vex", bg: "2EE38A", code: "MK-07" },
  { style: "pixel-art", seed: "Atlas-RX", bg: "00F0FF", code: "PX-14" },
  { style: "bottts", seed: "Nova-9", bg: "3E7A5C", code: "BT-09" },
  { style: "avataaars", seed: "Echo-Prime", bg: "FF6B00", code: "AV-22" },
  { style: "pixel-art", seed: "Riot-Z", bg: "39FF14", code: "PX-33" },
  { style: "bottts", seed: "ZK-09", bg: "00F0FF", code: "BT-44" },
  { style: "adventurer", seed: "Sora-X", bg: "FFD600", code: "AN-51" },
  { style: "thumbs", seed: "Vex-Glitch", bg: "2EE38A", code: "GL-66" },
  { style: "bottts", seed: "Kira-12", bg: "3E7A5C", code: "BT-78" },
  { style: "pixel-art", seed: "Jin-Synth", bg: "FF6B00", code: "PX-89" },
  { style: "personas", seed: "Bee-Holo", bg: "39FF14", code: "HP-91" },
  { style: "avataaars", seed: "Rune-Prime", bg: "00F0FF", code: "AV-94" },
];

const ACTIVITY = [
  { name: "Luna", action: "won +247 USDT in Crash Arena", tag: "ZEN" },
  { name: "Atlas", action: "founded a new crew: NEON LORDS", tag: "NEON" },
  { name: "Nova", action: "upgraded to Premium", tag: "VOID" },
  { name: "Echo", action: "joined the ZEN crew", tag: "ZEN" },
  { name: "Riot", action: "cashed out Crash at 18.4×", tag: "RIOT" },
  { name: "ZK-09", action: "entered the Crash Arena", tag: "BOTS" },
  { name: "Sora", action: "claimed the Gold pass", tag: "GOLD" },
  { name: "Vex", action: "shared a new avatar", tag: "ZEN" },
];

const WORLDS = [
  {
    id: "crash",
    name: "Crash Arena",
    sub: "The curve climbs. You pick the exit.",
    img: "https://images.pexels.com/photos/31729749/pexels-photo-31729749.jpeg?auto=compress&cs=tinysrgb&w=900",
    status: "LIVE",
    to: "/game",
    color: "from-brand-magenta to-brand-amber",
    online: 142,
    big: true,
  },
  {
    id: "limbo",
    name: "Limbo",
    sub: "Pick a number. One shot, all-in.",
    img: "https://images.unsplash.com/photo-1682370992900-d2ba92e09d7b?auto=format&fit=crop&w=900&q=70",
    status: "LIVE",
    to: "/limbo",
    color: "from-brand-cyan to-brand-violet",
    online: 58,
  },
  {
    id: "tower",
    name: "Tower Climb",
    sub: "Safe tile or trap. Climb 8 floors.",
    img: "https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=900&q=70",
    status: "LIVE",
    to: "/tower",
    color: "from-brand-violet to-brand-cyan",
    online: 37,
  },
  {
    id: "mines",
    name: "Mines Field",
    sub: "5×5 minefield. Greed pulls the trigger.",
    img: "https://images.unsplash.com/photo-1726500087571-d3193fb2b6b3?auto=format&fit=crop&w=900&q=70",
    status: "SOON",
    to: null,
    color: "from-brand-violet to-brand-magenta",
    online: 0,
  },
  {
    id: "plinko",
    name: "Plinko Drop",
    sub: "Ball drops, fate decides. Up to 1000× rewards.",
    img: "https://images.pexels.com/photos/17195067/pexels-photo-17195067.jpeg?auto=compress&cs=tinysrgb&w=900",
    status: "SOON",
    to: null,
    color: "from-brand-amber to-brand-magenta",
    online: 0,
  },
];

const FAQ = [
  { q: "What is GAME4YOU?", a: "GAME4YOU.NET is a social metaverse gaming hub. Pick a futuristic character, form a crew with friends, and play USDT-powered games together. Provably-fair, transparent on-chain wallet." },
  { q: "How do I customize my avatar?", a: "After signup, head to /profile and pick from a curated roster of cyberpunk humans and creatures — each with their own role and signature look." },
  { q: "What is a crew?", a: "Form a crew, pick a tag, invite your friends. Hang out in your private chat and climb the crew leaderboard together." },
  { q: "How do withdrawals work?", a: "USDT via the ERC20 network. 100 USDT minimum. Admin-approved and sent to your wallet within 24 hours." },
];

export default function Landing() {
  const [activityIdx, setActivityIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActivityIdx((i) => (i + 1) % ACTIVITY.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="landing-page">
      {/* Decorative orbs */}
      <div aria-hidden className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-brand-magenta/20 blur-[120px]" />
      <div aria-hidden className="pointer-events-none absolute top-40 -right-40 w-[520px] h-[520px] rounded-full bg-brand-cyan/15 blur-[140px]" />

      {/* LIVE ACTIVITY STRIP */}
      <div className="relative border-b border-white/10 bg-ink-800/40 backdrop-blur-md" data-testid="live-activity">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-4 text-sm">
          <span className="dot-live shrink-0" />
          <span className="label-mono text-brand-lime shrink-0">LIVE</span>
          <div className="flex-1 overflow-hidden">
            <motion.div
              key={activityIdx}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <span className="px-2 py-0.5 rounded-full bg-brand-magenta/20 text-brand-magenta text-xs font-mono">[{ACTIVITY[activityIdx].tag}]</span>
              <span className="text-white font-medium">{ACTIVITY[activityIdx].name}</span>
              <span className="text-white/50">→</span>
              <span className="text-white/80">{ACTIVITY[activityIdx].action}</span>
            </motion.div>
          </div>
          <div className="hidden sm:flex items-center gap-3 shrink-0 text-white/60 text-xs font-mono">
            <span><Users className="w-3 h-3 inline mr-1 text-brand-cyan" />842 online</span>
            <span><Trophy className="w-3 h-3 inline mr-1 text-brand-amber" />47 active crews</span>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 glass px-3.5 py-1.5 rounded-full mb-6"
            >
              <Sparkles className="w-3.5 h-3.5 text-brand-cyan" />
              <span className="label-mono !text-white/80">v2 · social metaverse</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display font-extrabold text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.92] tracking-tighter"
            >
              Your <span className="gradient-text">universe.</span>
              <br />
              Your <span className="gradient-text">rules.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-7 text-white/70 max-w-xl text-lg leading-relaxed"
            >
              Build your avatar. Form your crew. Conquer the worlds. <span className="text-white">GAME4YOU.NET</span> — the next-gen social gaming arena powered by USDT.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mt-9 flex flex-wrap gap-3"
            >
              <Link to="/login" className="btn-primary" data-testid="hero-join-btn">
                <Gamepad2 className="w-4 h-4" /> Enter the Arena
              </Link>
              <Link to="/crews" className="btn-secondary" data-testid="hero-crew-btn">
                <Users className="w-4 h-4" /> Form a Crew
              </Link>
              <Link to="/membership" className="btn-ghost" data-testid="hero-vip-btn">
                <Crown className="w-4 h-4 text-brand-amber" /> Access Passes
              </Link>
            </motion.div>

            {/* Inline stat strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-12 grid grid-cols-3 gap-3"
            >
              {[
                { label: "Total rewards", value: "847K", unit: "USDT" },
                { label: "Active crews", value: "47", unit: "squads" },
                { label: "Characters", value: "16", unit: "humans + xenos" },
              ].map((s) => (
                <div key={s.label} className="card-glass p-4">
                  <div className="label-mono">{s.label}</div>
                  <div className="font-display font-bold text-3xl mt-1 text-white">{s.value}</div>
                  <div className="text-xs font-mono text-white/40 mt-0.5">{s.unit}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* RIGHT — Avatar showcase scene */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
              {/* Glow halo */}
              <div className="absolute inset-0 rounded-[40%] bg-gradient-to-br from-brand-magenta/40 via-brand-violet/30 to-brand-cyan/30 blur-3xl" />

              {/* Center hero character with HUD frame */}
              <motion.div
                animate={{ y: [0, -14, 0] }}
                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                className="absolute inset-x-10 top-8 bottom-10 holo-frame rounded-[28px] overflow-hidden"
                style={{ background: `linear-gradient(180deg, #${HERO_CHAR.accent}22, #0D0A1C 80%)` }}
              >
                <img
                  src={HERO_CHAR.img}
                  alt={HERO_CHAR.name}
                  className="w-full h-full object-cover"
                />
                {/* HUD overlay text */}
                <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
                  <span className="hud-chip">UNIT_{HERO_CHAR.id.toUpperCase()}</span>
                  <span className="hud-chip" style={{ color: `#${HERO_CHAR.accent}`, borderColor: `#${HERO_CHAR.accent}80`, background: `#${HERO_CHAR.accent}22` }}>{HERO_CHAR.role.toUpperCase()}</span>
                </div>
                <div className="absolute bottom-3 left-3 right-3 z-10 flex items-end justify-between">
                  <div>
                    <div className="font-display font-extrabold text-2xl text-white drop-shadow-lg">{HERO_CHAR.name}</div>
                    <div className="text-xs font-mono text-white/80 drop-shadow">{HERO_CHAR.tag}</div>
                  </div>
                  <span className="hud-chip" style={{ color: "#39FF14", borderColor: "rgba(57,255,20,0.5)", background: "rgba(57,255,20,0.12)" }}>● ONLINE</span>
                </div>
              </motion.div>

              {/* Floating mini characters */}
              {FLOAT_CHARS.map((char, idx) => {
                const positions = [
                  { class: "-left-2 top-6 w-24 h-24", duration: 4.2 },
                  { class: "-right-2 top-24 w-28 h-28", duration: 5.4 },
                  { class: "-left-4 bottom-10 w-24 h-24", duration: 6 },
                ];
                const pos = positions[idx];
                return (
                  <motion.div
                    key={char.id}
                    animate={{ y: [0, idx === 1 ? -10 : 12, 0] }}
                    transition={{ repeat: Infinity, duration: pos.duration, ease: "easeInOut" }}
                    className={`absolute ${pos.class} holo-frame rounded-2xl overflow-hidden`}
                    style={{ background: `#${char.accent}22` }}
                  >
                    <img src={char.img} alt={char.name} className="w-full h-full object-cover" />
                    <div className="absolute bottom-1 left-1 right-1 z-10">
                      <div className="text-[9px] font-mono text-white font-bold drop-shadow">{char.name}</div>
                    </div>
                  </motion.div>
                );
              })}

              {/* Crew badge floating bottom */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.5 }}
                className="absolute -bottom-2 left-1/2 -translate-x-1/2 glass rounded-full px-4 py-2.5 flex items-center gap-2 whitespace-nowrap z-20"
              >
                <Users className="w-3.5 h-3.5 text-brand-cyan" />
                <span className="text-xs font-mono text-white">Crew [NEON]</span>
                <span className="text-white/30">·</span>
                <span className="text-xs font-mono text-brand-lime">+12 members</span>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* AVATAR GALLERY STRIP */}
      <section className="relative py-14 lg:py-20 border-y border-white/5 bg-ink-900/40" data-testid="avatar-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="label-mono mb-3 text-brand-cyan">// build yourself</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tighter">Pick an avatar. <span className="gradient-text">Be you.</span></h2>
            <p className="text-white/60 mt-3 max-w-xl">8 unique styles, infinite variations. From hair to outfit — every detail is yours.</p>
          </div>
          <Link to="/login" className="btn-secondary self-start sm:self-auto" data-testid="cta-customize-avatar">
            <Wand2 className="w-4 h-4" /> Customize
          </Link>
        </div>

        {/* Auto-scrolling avatar marquee with HUD frames — real cyberpunk characters */}
        <div className="relative overflow-hidden">
          <div className="marquee-track flex gap-5 w-max">
            {[...SHOWCASE_AVATARS, ...SHOWCASE_AVATARS].map((a, i) => (
              <div
                key={`${a.id}-${i}`}
                className="w-40 h-56 sm:w-48 sm:h-64 shrink-0 holo-frame rounded-3xl overflow-hidden relative group"
                style={{ background: `linear-gradient(180deg, #${a.accent}22, #0D0A1C 70%)` }}
              >
                <img
                  src={a.img}
                  alt={a.name}
                  className="w-full h-full object-cover transition-transform group-hover:scale-110"
                  loading="lazy"
                />
                {/* HUD overlay */}
                <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
                  <span className="hud-chip">{a.code}</span>
                  <span className="hud-chip" style={{ color: a.type === "creature" ? "#FF003C" : "#39FF14", borderColor: a.type === "creature" ? "rgba(255,0,60,0.5)" : "rgba(57,255,20,0.5)", background: a.type === "creature" ? "rgba(255,0,60,0.12)" : "rgba(57,255,20,0.12)" }}>
                    {a.type === "creature" ? "XENO" : "HUMAN"}
                  </span>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-ink-900 via-ink-900/85 to-transparent z-10">
                  <div className="text-sm font-display font-bold text-white tracking-tight">{a.name}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: `#${a.accent}` }} />
                    <div className="label-mono !text-[8px] !tracking-widest !text-white/70">{a.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Fade edges */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-ink-800 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-ink-800 to-transparent" />
        </div>
      </section>

      {/* WORLDS GRID */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid="worlds-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
          <div>
            <div className="label-mono mb-3 text-brand-magenta">// explore the worlds</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tighter">Four worlds. <span className="gradient-text">One wallet.</span></h2>
            <p className="text-white/60 mt-3">Different strategy in every world. All powered by USDT.</p>
          </div>
          <Link to="/games" className="btn-ghost self-start sm:self-auto" data-testid="cta-all-worlds">
            See all <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6">
          <WorldCard {...WORLDS[0]} className="md:col-span-7 md:row-span-2 md:min-h-[460px]" />
          <WorldCard {...WORLDS[1]} className="md:col-span-5 md:min-h-[220px]" />
          <WorldCard {...WORLDS[2]} className="md:col-span-3 md:min-h-[220px]" small />
          <WorldCard {...WORLDS[3]} className="md:col-span-2 md:min-h-[220px]" small />
        </div>
      </section>

      {/* CREW SHOWCASE — LIGHT ZONE */}
      <section className="relative my-16 lg:my-20" data-testid="crew-section">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-pearl via-white to-brand-pearl opacity-95" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 text-ink-800">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="label-mono mb-3 !text-ink-800/60">// crew</div>
              <h2 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tighter text-ink-800">
                Don&apos;t go solo. <br />
                <span className="text-brand-magenta">Form a crew.</span>
              </h2>
              <p className="mt-5 text-ink-800/70 text-lg max-w-lg leading-relaxed">
                Pick your tag, invite your friends, and strategize together in private chat. Climb the crew leaderboard for the top spot.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {[
                  { icon: MessagesSquare, t: "Private chat", s: "Members only" },
                  { icon: Trophy, t: "Crew leaderboard", s: "Weekly rewards" },
                  { icon: Users, t: "Unlimited members", s: "Invite link" },
                  { icon: Shield, t: "Custom tag", s: "Max 5 chars" },
                ].map((f) => {
                  const Ic = f.icon;
                  return (
                    <div key={f.t} className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-brand-magenta to-brand-amber grid place-items-center shrink-0 shadow-glow-magenta">
                        <Ic className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-display font-bold text-ink-800">{f.t}</div>
                        <div className="text-sm text-ink-800/60">{f.s}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Link to="/crews" className="btn-primary mt-9 inline-flex" data-testid="cta-crew-section">
                <Users className="w-4 h-4" /> Explore Crews
              </Link>
            </div>

            {/* RIGHT — crew chat mock */}
            <div className="lg:col-span-6 relative">
              <div className="relative bg-ink-800 rounded-3xl p-5 shadow-2xl border border-ink-700 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-magenta/10 via-transparent to-brand-cyan/10" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-magenta to-brand-violet grid place-items-center text-white font-display font-bold text-sm">N</div>
                      <div>
                        <div className="font-display font-bold text-white text-sm">NEON LORDS</div>
                        <div className="text-xs text-white/50 font-mono">[NEON] · 12 members</div>
                      </div>
                    </div>
                    <span className="dot-live" />
                  </div>
                  <div className="space-y-3">
                    {[
                      { from: "Atlas RX", text: "30× this round, I can feel it", id: "atlas-rx" },
                      { from: "Kira Zero", text: "auto-cashout set at 10×", id: "kira-zero" },
                      { from: "Xenon-9", text: "🚀 same here", id: "xenon-9" },
                      { from: "Ronin 7", text: "let&apos;s go crew, push push push", id: "ronin-7" },
                    ].map((m, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <img
                          src={buildAvatarUrl({ character_id: m.id })}
                          alt={m.from}
                          className="w-8 h-8 rounded-full border border-white/10 shrink-0 object-cover"
                        />
                        <div className="flex-1">
                          <div className="text-xs font-mono text-white/60">{m.from}</div>
                          <div className="text-sm text-white/90 bg-white/5 rounded-2xl rounded-tl-md px-3 py-1.5 inline-block">{m.text}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Decoration */}
              <div className="absolute -top-6 -right-4 w-24 h-24 rounded-full bg-brand-cyan/30 blur-2xl" />
              <div className="absolute -bottom-6 -left-4 w-32 h-32 rounded-full bg-brand-magenta/30 blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP PASSES */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20" data-testid="passes-section">
        <div className="text-center mb-12">
          <div className="label-mono mb-3 text-brand-amber">// access passes</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tighter">Your <span className="gradient-text">metaverse passes.</span></h2>
          <p className="text-white/60 mt-3 max-w-xl mx-auto">Lower platform fees. Higher cashback. More perks.</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-7">
          <PassCard
            name="Basic"
            price="0"
            perks={["Access to all worlds", "3% platform fee", "Standard fees", "Avatar customization"]}
            color="from-white/15 to-white/5"
            border="border-white/15"
          />
          <PassCard
            name="Premium"
            price="50"
            perks={["2.5% platform fee", "50% off fees", "2% weekly cashback", "Premium badge"]}
            color="from-brand-magenta/40 to-brand-violet/30"
            border="border-brand-magenta/50"
            popular
          />
          <PassCard
            name="Gold"
            price="200"
            perks={["2% platform fee", "Zero fees", "5% cashback", "VIP crew chat", "Early access"]}
            color="from-brand-amber/40 to-brand-magenta/30"
            border="border-brand-amber/50"
            gold
          />
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { i: Shield, t: "Provably Fair", s: "SHA-256 hash & reveal" },
            { i: Zap, t: "USDT Instant", s: "ERC20 deposits / withdrawals" },
            { i: Globe2, t: "Social Hub", s: "Crew, chat, leaderboard" },
            { i: Bot, t: "Character Studio", s: "Humans · creatures · roles" },
          ].map((b) => {
            const Ic = b.i;
            return (
              <div key={b.t} className="card-glass p-5 noise" data-testid={`trust-${b.t}`}>
                <Ic className="w-6 h-6 text-brand-cyan mb-3" />
                <div className="font-display font-bold text-white">{b.t}</div>
                <div className="text-xs font-mono text-white/50 mt-1">{b.s}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="relative max-w-4xl mx-auto px-4 sm:px-6 py-16 lg:py-20" data-testid="faq-section">
        <div className="text-center mb-10">
          <div className="label-mono mb-3 text-brand-violet">// FAQ</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tighter">Answers.</h2>
        </div>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <details key={i} className="group card-glass p-5 cursor-pointer">
              <summary className="list-none flex items-center justify-between gap-4">
                <span className="font-display font-bold text-lg text-white">{f.q}</span>
                <span className="w-8 h-8 rounded-full border border-white/15 grid place-items-center text-white/70 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-white/70 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="relative border-t border-white/10 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="font-display font-extrabold text-2xl text-white">GAME<span className="gradient-text">4YOU</span>.NET</div>
              <p className="text-white/50 text-sm mt-2 max-w-xs">Social gaming metaverse · play together, earn together</p>
            </div>
            <div>
              <div className="label-mono mb-3">Platform</div>
              <ul className="space-y-1.5 text-sm text-white/70">
                <li><Link to="/games" className="hover:text-white">Worlds</Link></li>
                <li><Link to="/crews" className="hover:text-white">Crews</Link></li>
                <li><Link to="/leaderboard" className="hover:text-white">Leaderboard</Link></li>
              </ul>
            </div>
            <div>
              <div className="label-mono mb-3">Account</div>
              <ul className="space-y-1.5 text-sm text-white/70">
                <li><Link to="/login" className="hover:text-white">Sign in</Link></li>
                <li><Link to="/wallet" className="hover:text-white">Wallet</Link></li>
                <li><Link to="/membership" className="hover:text-white">Access passes</Link></li>
              </ul>
            </div>
            <div>
              <div className="label-mono mb-3">Security</div>
              <ul className="space-y-1.5 text-sm text-white/70">
                <li>Provably Fair</li>
                <li>USDT ERC20</li>
                <li>Fair Play · Community First</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-10 pt-6 text-xs text-white/40 font-mono flex flex-col sm:flex-row justify-between gap-2">
            <div>© {new Date().getFullYear()} GAME4YOU.NET · All rights reserved</div>
            <div>not financial advice</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function WorldCard({ id, name, sub, img, status, to, color, online, big, small, className = "" }) {
  const live = status === "LIVE";
  const Wrapper = live && to ? Link : "div";
  const props = live && to ? { to } : {};
  return (
    <Wrapper
      {...props}
      data-testid={`world-card-${id}`}
      className={`group relative overflow-hidden rounded-3xl beam-border ${className} ${live ? "cursor-pointer" : "cursor-default"}`}
    >
      <img
        src={img}
        alt={name}
        className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${live ? "group-hover:scale-110" : "opacity-70"}`}
      />
      <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-50 mix-blend-multiply`} />
      <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />

      <div className={`relative h-full flex flex-col justify-between ${small ? "p-5" : big ? "p-7 lg:p-9" : "p-6"}`}>
        <div className="flex items-start justify-between gap-2">
          {live ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-lime/15 text-brand-lime label-mono !text-[10px]">
              <span className="dot-live !w-1.5 !h-1.5" /> {online} online
            </span>
          ) : (
            <span className="px-2.5 py-1 rounded-full bg-white/10 text-white/70 label-mono !text-[10px]">{status}</span>
          )}
          {big && <span className="label-mono !text-white/70">// {String(id).toUpperCase()}_01</span>}
        </div>
        <div>
          <h3 className={`font-display font-extrabold tracking-tighter ${small ? "text-xl" : big ? "text-5xl lg:text-6xl" : "text-3xl"}`}>{name}</h3>
          {!small && <p className="text-white/80 text-sm sm:text-base mt-2 max-w-sm">{sub}</p>}
          {live && (
            <div className="mt-4 inline-flex items-center gap-1.5 text-brand-cyan font-display font-bold group-hover:translate-x-1 transition-transform">
              Play now <ChevronRight className="w-4 h-4" />
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
}

function PassCard({ name, price, perks, color, border, popular, gold }) {
  return (
    <div className={`relative rounded-3xl border ${border} bg-gradient-to-br ${color} p-7 overflow-hidden noise`} data-testid={`pass-${String(name).toLowerCase()}`}>
      {popular && <div className="absolute top-5 right-5 px-2.5 py-1 rounded-full bg-white text-ink-800 text-xs font-mono font-bold">POPULAR</div>}
      {gold && <div className="absolute top-5 right-5 px-2.5 py-1 rounded-full bg-brand-amber text-ink-800 text-xs font-mono font-bold">VIP</div>}
      <div className="font-display font-extrabold text-2xl text-white">{name}</div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="font-display font-extrabold text-5xl text-white">{price === "0" ? "Free" : price}</span>
        {price !== "0" && <span className="text-white/60 text-sm font-mono">USDT / mo</span>}
      </div>
      <ul className="mt-6 space-y-2.5">
        {perks.map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/85">
            <Sparkles className="w-3.5 h-3.5 mt-1 text-brand-cyan shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <Link to="/membership" className="btn-secondary w-full mt-7" data-testid={`pass-cta-${String(name).toLowerCase()}`}>Learn more</Link>
    </div>
  );
}
